import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getLanguageFromPath, getLocalizedPath, type Language } from "@/lib/language";
import { nav } from "@/lib/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const language = getLanguageFromPath(location.pathname);
  const copy = nav[language];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    navigate(getLocalizedPath(language, href));
  };

  const switchLanguage = (nextLanguage: Language) => {
    setMenuOpen(false);
    const hash = window.location.hash || location.hash;
    const projectMatch = location.pathname.match(/^(?:\/en)?\/portfolio\/([^/]+)/);
    if (projectMatch) {
      navigate(`${nextLanguage === "en" ? "/en" : ""}/portfolio/${projectMatch[1]}${hash}`);
      return;
    }
    navigate(getLocalizedPath(nextLanguage, hash));
  };

  const LanguageToggle = ({ mobile = false }: { mobile?: boolean }) => (
    <div
      className={`flex items-center ${mobile ? "gap-2 pt-1" : "gap-1"}`}
      aria-label="Language selector"
    >
      {(["hu", "en"] as Language[]).map((lang) => {
        const active = language === lang;
        return (
          <button
            key={lang}
            type="button"
            onClick={() => switchLanguage(lang)}
            className={`${mobile ? "px-3 py-2" : "px-2.5 py-1.5"} rounded-full text-xs font-semibold tracking-widest transition-all duration-200 cursor-pointer`}
            style={
              active
                ? { backgroundColor: "#acd1c6", color: "#004643" }
                : { border: "1px solid rgba(172,209,198,0.35)", color: "rgba(255,255,255,0.75)" }
            }
            aria-current={active ? "true" : undefined}
          >
            {lang.toUpperCase()}
          </button>
        );
      })}
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolled ? "backdrop-blur-md" : ""}`}
      style={scrolled ? { backgroundColor: "rgba(0,70,67,0.75)", boxShadow: "0 1px 20px rgba(0,70,67,0.3)" } : {}}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); handleNavClick("#home"); }}
          className="flex items-center gap-2 cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/81dfed5541ea1ba24f9dd9a3c33062c9.png"
            alt="iVision Design Logo"
            className="h-24 md:h-32 w-auto object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-8">
          {copy.links.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap text-white/80 hover:text-white"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <LanguageToggle />
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNavClick("#contact"); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#acd1c6", color: "#004643" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#9bc4b8"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#acd1c6"; }}
          >
            {copy.cta}
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={copy.menu}
        >
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? "opacity-0" : ""}`} />
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
        style={{ backgroundColor: "#004643" }}
      >
        <ul className="flex flex-col px-6 py-4 gap-4">
          {copy.links.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="block text-white/80 font-medium text-base py-1 cursor-pointer hover:text-white transition-colors"
              >
                {link.label}
              </a>
            </li>
          ))}
          <li>
            <LanguageToggle mobile />
          </li>
          <li>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick("#contact"); }}
              className="inline-block mt-2 px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: "#acd1c6", color: "#004643" }}
            >
              {copy.cta}
            </a>
          </li>
        </ul>
      </div>
    </header>
  );
}
